import { useState, useEffect, useCallback } from 'react'
import { FaDesktop, FaKeyboard, FaTimes } from 'react-icons/fa'
import './KeyboardTester.css'

function KeyboardTester() {
  const [pressedKeys, setPressedKeys] = useState(new Set())
  const [keyHistory, setKeyHistory] = useState([])
  const [isTesting, setIsTesting] = useState(false)

  const handleKeyDown = useCallback((event) => {
    event.preventDefault()
    
    const keyCombo = {
      key: event.key,
      code: event.code,
      ctrlKey: event.ctrlKey,
      altKey: event.altKey,
      shiftKey: event.shiftKey,
      metaKey: event.metaKey,
      timestamp: Date.now()
    }

    setPressedKeys(prev => new Set([...prev, event.code]))
    
    setKeyHistory(prev => {
      const newHistory = [keyCombo, ...prev].slice(0, 50)
      return newHistory
    })
  }, [])

  const handleKeyUp = useCallback((event) => {
    setPressedKeys(prev => {
      const newSet = new Set(prev)
      newSet.delete(event.code)
      return newSet
    })
  }, [])

  useEffect(() => {
    if (isTesting) {
      window.addEventListener('keydown', handleKeyDown)
      window.addEventListener('keyup', handleKeyUp)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [isTesting, handleKeyDown, handleKeyUp])

  const clearHistory = () => {
    setKeyHistory([])
    setPressedKeys(new Set())
  }

  const getKeyDisplay = (keyCombo) => {
    const parts = []
    if (keyCombo.ctrlKey) parts.push('Ctrl')
    if (keyCombo.altKey) parts.push('Alt')
    if (keyCombo.shiftKey) parts.push('Shift')
    if (keyCombo.metaKey) parts.push('Meta')
    parts.push(keyCombo.key === ' ' ? 'Space' : keyCombo.key)
    return parts.join(' + ')
  }

  const keyboardLayout = [
    ['Escape', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'],
    ['Backquote', 'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9', 'Digit0', 'Minus', 'Equal', 'Backspace'],
    ['Tab', 'KeyQ', 'KeyW', 'KeyE', 'KeyR', 'KeyT', 'KeyY', 'KeyU', 'KeyI', 'KeyO', 'KeyP', 'BracketLeft', 'BracketRight', 'Backslash'],
    ['CapsLock', 'KeyA', 'KeyS', 'KeyD', 'KeyF', 'KeyG', 'KeyH', 'KeyJ', 'KeyK', 'KeyL', 'Semicolon', 'Quote', 'Enter'],
    ['ShiftLeft', 'KeyZ', 'KeyX', 'KeyC', 'KeyV', 'KeyB', 'KeyN', 'KeyM', 'Comma', 'Period', 'Slash', 'ShiftRight'],
    ['ControlLeft', 'MetaLeft', 'AltLeft', 'Space', 'AltRight', 'MetaRight', 'ContextMenu', 'ControlRight']
  ]

  const getKeyLabel = (code) => {
    const labels = {
      'Escape': 'Esc', 'Backquote': '`', 'Minus': '-', 'Equal': '=', 'BracketLeft': '[',
      'BracketRight': ']', 'Backslash': '\\', 'Semicolon': ';', 'Quote': "'",
      'Comma': ',', 'Period': '.', 'Slash': '/', 'ShiftLeft': 'Shift', 'ShiftRight': 'Shift',
      'ControlLeft': 'Ctrl', 'ControlRight': 'Ctrl', 'AltLeft': 'Alt', 'AltRight': 'Alt',
      'MetaLeft': 'Win', 'MetaRight': 'Win', 'ContextMenu': 'Menu', 'CapsLock': 'Caps',
      'Backspace': '←', 'Tab': 'Tab', 'Enter': '↵', 'Space': 'Space'
    }
    
    if (labels[code]) return labels[code]
    if (code.startsWith('Digit')) return code.replace('Digit', '')
    if (code.startsWith('Key')) return code.replace('Key', '')
    if (code.startsWith('F') && code.length <= 3) return code
    return code
  }

  return (
    <div className="keyboard-tester">
      <div className="platform-notice">
        <span className="platform-support desktop-only">
          <FaDesktop /> 此功能仅电脑端可用
        </span>
      </div>

      {!isTesting ? (
        <div className="keyboard-start">
          <FaKeyboard className="keyboard-icon" />
          <h3>键盘测试工具</h3>
          <p>点击下方按钮开始测试键盘按键</p>
          <p className="hint">支持检测组合键（Ctrl、Alt、Shift 等）</p>
          <button className="start-btn" onClick={() => setIsTesting(true)}>
            开始测试
          </button>
        </div>
      ) : (
        <div className="keyboard-active">
          <div className="keyboard-header">
            <h3>键盘测试中...</h3>
            <button className="stop-btn" onClick={() => setIsTesting(false)}>
              <FaTimes /> 停止测试
            </button>
          </div>

          <div className="virtual-keyboard">
            {keyboardLayout.map((row, rowIndex) => (
              <div key={rowIndex} className="keyboard-row">
                {row.map((keyCode) => (
                  <div
                    key={keyCode}
                    className={`key ${pressedKeys.has(keyCode) ? 'pressed' : ''}`}
                    data-code={keyCode}
                  >
                    {getKeyLabel(keyCode)}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="key-history">
            <div className="history-header">
              <h4>按键历史</h4>
              <button className="clear-btn" onClick={clearHistory}>
                清空历史
              </button>
            </div>
            {keyHistory.length === 0 ? (
              <p className="no-history">暂无按键记录</p>
            ) : (
              <ul className="history-list">
                {keyHistory.map((item, index) => (
                  <li key={index} className="history-item">
                    <span className="key-combo">{getKeyDisplay(item)}</span>
                    <span className="key-code">{item.code}</span>
                    <span className="timestamp">
                      {new Date(item.timestamp).toLocaleTimeString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default KeyboardTester